import clsx from 'clsx';
import { de, enUS, es, fr, pt } from 'date-fns/locale';
import {
  CalendarIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  XIcon,
} from 'lucide-react';
import { DateRange, DayPicker, Locale } from 'react-day-picker';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useTranslation } from '@splitfiesta/i18n';

import { getInputClasses } from '@/lib/styles/form';

import '../../styles/datepicker.css';

// Static locale map for date-fns
const DATE_FNS_LOCALES: Record<string, Locale> = {
  en: enUS,
  es: es,
  fr: fr,
  pt: pt,
  de: de,
};

interface DateRangePickerProps {
  id?: string;
  startDate: string; // YYYY-MM-DD format
  endDate: string; // YYYY-MM-DD format
  onStartDateChange: (date: string) => void;
  onEndDateChange: (date: string) => void;
  startLabel?: string;
  endLabel?: string;
  startPlaceholder?: string;
  endPlaceholder?: string;
  disabled?: boolean;
  required?: boolean;
  min?: string; // YYYY-MM-DD format
  max?: string; // YYYY-MM-DD format
  className?: string;
  showPresets?: boolean;
  monthOnly?: boolean; // Display months only (e.g., "Dec 2024" instead of "Dec 1, 2024")
  'data-testid'?: string;
}

/**
 * Get short month names for a locale
 */
function getMonthNames(locale: string): string[] {
  const months: string[] = [];
  for (let i = 0; i < 12; i++) {
    const date = new Date(2024, i, 1);
    months.push(
      date.toLocaleDateString(locale, { month: 'short' }).replace('.', '')
    );
  }
  return months;
}

/**
 * Parses a YYYY-MM-DD string into a Date object (local time)
 */
function parseDate(dateString: string): Date | undefined {
  if (!dateString) return undefined;
  const [year, month, day] = dateString.split('-').map(Number);
  return new Date(year, month - 1, day);
}

type PresetId =
  'thisMonth' | 'lastMonth' | 'last3Months' | 'thisYear' | 'lastYear';

/**
 * Calculate date range for a preset (month-based presets only)
 */
function getPresetRange(presetId: PresetId): { from: Date; to: Date } {
  const today = new Date();
  const currentYear = today.getFullYear();
  const currentMonth = today.getMonth();

  switch (presetId) {
    case 'thisMonth': {
      const from = new Date(currentYear, currentMonth, 1);
      const to = new Date(currentYear, currentMonth + 1, 0);
      return { from, to };
    }
    case 'lastMonth': {
      const from = new Date(currentYear, currentMonth - 1, 1);
      const to = new Date(currentYear, currentMonth, 0);
      return { from, to };
    }
    case 'last3Months': {
      const from = new Date(currentYear, currentMonth - 2, 1);
      const to = new Date(currentYear, currentMonth + 1, 0);
      return { from, to };
    }
    case 'thisYear': {
      const from = new Date(currentYear, 0, 1);
      const to = new Date(currentYear, 11, 31);
      return { from, to };
    }
    case 'lastYear': {
      const from = new Date(currentYear - 1, 0, 1);
      const to = new Date(currentYear - 1, 11, 31);
      return { from, to };
    }
  }
}

/**
 * Formats a Date object into YYYY-MM-DD string
 */
function formatDateToString(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function DateRangePicker({
  id,
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  startLabel,
  endLabel,
  startPlaceholder,
  endPlaceholder,
  disabled = false,
  required = false,
  min,
  max,
  className = '',
  showPresets = false,
  monthOnly = false,
  'data-testid': testId,
}: DateRangePickerProps) {
  const { t, i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Month picker state
  const [displayYear, setDisplayYear] = useState(() => {
    if (startDate) return parseInt(startDate.substring(0, 4));
    return new Date().getFullYear();
  });
  const [selectingEnd, setSelectingEnd] = useState(false);

  // Update displayYear when picker opens to show the current selection
  const handleOpen = useCallback(() => {
    if (!disabled) {
      if (startDate && monthOnly) {
        setDisplayYear(parseInt(startDate.substring(0, 4)));
      }
      setIsOpen(prev => !prev);
    }
  }, [disabled, startDate, monthOnly]);

  // Get month names for current locale
  const monthNames = useMemo(
    () => getMonthNames(i18n?.language || 'en'),
    [i18n?.language]
  );

  // Get date-fns locale based on current language
  const locale = useMemo(() => {
    const language = i18n?.language || 'en';
    return DATE_FNS_LOCALES[language] || DATE_FNS_LOCALES.en;
  }, [i18n?.language]);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () =>
        document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
        buttonRef.current?.focus();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen]);

  const startDateParsed = parseDate(startDate);
  const endDateParsed = parseDate(endDate);
  const minDate = parseDate(min || '');
  const maxDate = parseDate(max || '');

  const selectedRange: DateRange | undefined =
    startDateParsed || endDateParsed
      ? { from: startDateParsed, to: endDateParsed }
      : undefined;

  const handleSelect = useCallback(
    (range: DateRange | undefined) => {
      if (range) {
        onStartDateChange(range.from ? formatDateToString(range.from) : '');
        onEndDateChange(range.to ? formatDateToString(range.to) : '');

        // Close the picker only when both dates are selected AND they are different
        // (first click sets both from and to to the same date)
        if (
          range.from &&
          range.to &&
          range.from.getTime() !== range.to.getTime()
        ) {
          setIsOpen(false);
        }
      } else {
        onStartDateChange('');
        onEndDateChange('');
      }
    },
    [onStartDateChange, onEndDateChange]
  );

  const handleClear = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onStartDateChange('');
      onEndDateChange('');
    },
    [onStartDateChange, onEndDateChange]
  );

  const handlePresetClick = useCallback(
    (presetId: PresetId) => {
      const range = getPresetRange(presetId);
      onStartDateChange(formatDateToString(range.from));
      onEndDateChange(formatDateToString(range.to));
      setIsOpen(false);
      setSelectingEnd(false);
    },
    [onStartDateChange, onEndDateChange]
  );

  // Handle month selection for month-only mode
  const handleMonthClick = useCallback(
    (monthIndex: number) => {
      const firstDay = new Date(displayYear, monthIndex, 1);
      const lastDay = new Date(displayYear, monthIndex + 1, 0);

      if (!selectingEnd || !startDate) {
        // Selecting start month
        onStartDateChange(formatDateToString(firstDay));
        onEndDateChange(formatDateToString(lastDay));
        setSelectingEnd(true);
      } else {
        // Selecting end month
        const startParsed = parseDate(startDate);
        if (startParsed) {
          const clickedDate = new Date(displayYear, monthIndex, 1);
          if (clickedDate < startParsed) {
            // Clicked before start - make this the new start
            onStartDateChange(formatDateToString(firstDay));
            onEndDateChange(formatDateToString(lastDay));
          } else {
            // Update end date to last day of selected month
            onEndDateChange(formatDateToString(lastDay));
            setIsOpen(false);
            setSelectingEnd(false);
          }
        }
      }
    },
    [displayYear, selectingEnd, startDate, onStartDateChange, onEndDateChange]
  );

  // Check if a month is in the selected range
  const isMonthInRange = useCallback(
    (monthIndex: number): 'start' | 'end' | 'middle' | null => {
      if (!startDate) return null;
      const startParsed = parseDate(startDate);
      const endParsed = parseDate(endDate);
      if (!startParsed) return null;

      const startYearMonth =
        startParsed.getFullYear() * 12 + startParsed.getMonth();
      const endYearMonth = endParsed
        ? endParsed.getFullYear() * 12 + endParsed.getMonth()
        : startYearMonth;
      const currentYearMonth = displayYear * 12 + monthIndex;

      if (
        currentYearMonth === startYearMonth &&
        currentYearMonth === endYearMonth
      ) {
        return 'start'; // Single month selected
      }
      if (currentYearMonth === startYearMonth) return 'start';
      if (currentYearMonth === endYearMonth) return 'end';
      if (
        currentYearMonth > startYearMonth &&
        currentYearMonth < endYearMonth
      ) {
        return 'middle';
      }
      return null;
    },
    [startDate, endDate, displayYear]
  );

  const presets = useMemo(
    () =>
      [
        { id: 'thisMonth', label: t('common:dateRange.presets.thisMonth') },
        { id: 'lastMonth', label: t('common:dateRange.presets.lastMonth') },
        { id: 'last3Months', label: t('common:dateRange.presets.last3Months') },
        { id: 'thisYear', label: t('common:dateRange.presets.thisYear') },
        { id: 'lastYear', label: t('common:dateRange.presets.lastYear') },
      ] as Array<{ id: PresetId; label: string }>,
    [t]
  );

  const formatDisplayDate = (date: Date | undefined): string => {
    if (!date) return '';
    return date.toLocaleDateString(i18n?.language || 'en', {
      year: 'numeric',
      month: 'short',
      ...(monthOnly ? {} : { day: 'numeric' }),
    });
  };

  const startDisplayValue = formatDisplayDate(startDateParsed);
  const endDisplayValue = formatDisplayDate(endDateParsed);
  const hasValue = startDate || endDate;

  // Build combined label
  const combinedLabel =
    startLabel && endLabel ? `${startLabel} - ${endLabel}` : startLabel;

  return (
    <div className={clsx('relative', className)} ref={containerRef}>
      {combinedLabel && (
        <label
          htmlFor={id}
          className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
        >
          {combinedLabel}
          {required && <span className="text-error-500 ml-1">*</span>}
        </label>
      )}

      <button
        ref={buttonRef}
        id={id}
        type="button"
        onClick={handleOpen}
        disabled={disabled}
        className={clsx(
          getInputClasses({ disabled }),
          'relative flex items-center pr-10 cursor-pointer text-left'
        )}
        data-testid={testId}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
      >
        <div className="flex items-center gap-1.5 flex-1 min-w-0">
          <span
            className={clsx(
              'truncate',
              !startDisplayValue && 'text-gray-400 dark:text-gray-500'
            )}
          >
            {startDisplayValue ||
              startPlaceholder ||
              t('common:placeholders.startDate')}
          </span>
          <span className="text-gray-400 dark:text-gray-500 flex-shrink-0 text-xs">
            {t('common:dateRange.to')}
          </span>
          <span
            className={clsx(
              'truncate',
              !endDisplayValue && 'text-gray-400 dark:text-gray-500'
            )}
          >
            {endDisplayValue ||
              endPlaceholder ||
              t('common:placeholders.endDate')}
          </span>
        </div>
        <div className="absolute inset-y-0 right-0 flex items-center pr-2 gap-0.5">
          {hasValue && !disabled && !required && (
            <span
              role="button"
              tabIndex={0}
              onClick={handleClear}
              onKeyDown={e =>
                e.key === 'Enter' &&
                handleClear(e as unknown as React.MouseEvent)
              }
              className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition-colors"
              aria-label={t('common:actions.clear')}
            >
              <XIcon className="w-4 h-4 text-gray-400" />
            </span>
          )}
          <CalendarIcon className="w-5 h-5 text-gray-400 pointer-events-none" />
        </div>
      </button>

      {isOpen && (
        <div
          className={clsx(
            'date-picker-popover absolute top-full mt-1 left-0 sm:left-0 w-full sm:w-auto',
            showPresets && 'flex flex-col sm:flex-row'
          )}
          role="dialog"
          aria-modal="true"
          aria-label={t('common:labels.dateRange')}
        >
          {showPresets && (
            <div className="flex flex-row sm:flex-col gap-1 p-2 sm:p-3 border-b sm:border-b-0 sm:border-r border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 rounded-t-lg sm:rounded-t-none sm:rounded-l-lg overflow-x-auto sm:overflow-x-visible sm:min-w-[140px]">
              {presets.map(preset => (
                <button
                  key={preset.id}
                  type="button"
                  onClick={() => handlePresetClick(preset.id)}
                  className="text-left px-2 sm:px-3 py-1.5 text-xs sm:text-sm rounded-md hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 transition-colors whitespace-nowrap shrink-0"
                >
                  {preset.label}
                </button>
              ))}
            </div>
          )}
          {monthOnly ? (
            <div className="p-3 bg-white dark:bg-gray-800 rounded-b-lg sm:rounded-b-none sm:rounded-r-lg w-full sm:min-w-[280px]">
              {/* Year navigation */}
              <div className="flex items-center justify-between mb-3">
                <button
                  type="button"
                  onClick={() => setDisplayYear(y => y - 1)}
                  className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                  aria-label="Previous year"
                >
                  <ChevronLeftIcon className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                </button>
                <span className="font-semibold text-gray-900 dark:text-gray-100">
                  {displayYear}
                </span>
                <button
                  type="button"
                  onClick={() => setDisplayYear(y => y + 1)}
                  className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                  aria-label="Next year"
                >
                  <ChevronRightIcon className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                </button>
              </div>
              {/* Month grid */}
              <div className="grid grid-cols-3 gap-1.5">
                {monthNames.map((month, index) => {
                  const rangeStatus = isMonthInRange(index);
                  return (
                    <button
                      key={index}
                      type="button"
                      onClick={() => handleMonthClick(index)}
                      className={clsx(
                        'px-2 sm:px-3 py-2 text-sm rounded-md transition-colors',
                        rangeStatus === 'start' &&
                          'bg-orange-500 text-white font-medium',
                        rangeStatus === 'end' &&
                          'bg-orange-500 text-white font-medium',
                        rangeStatus === 'middle' &&
                          'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300',
                        !rangeStatus &&
                          'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'
                      )}
                    >
                      {month}
                    </button>
                  );
                })}
              </div>
            </div>
          ) : (
            <DayPicker
              mode="range"
              selected={selectedRange}
              onSelect={handleSelect}
              locale={locale}
              disabled={[
                ...(minDate ? [{ before: minDate }] : []),
                ...(maxDate ? [{ after: maxDate }] : []),
              ]}
              defaultMonth={startDateParsed || endDateParsed || new Date()}
              showOutsideDays
              fixedWeeks
              numberOfMonths={1}
            />
          )}
        </div>
      )}
    </div>
  );
}

export default DateRangePicker;
